/**
 * Bulk attribute-intelligence research (task-fe0k6h2v4f). Turns "20 clicks with a
 * destructive checkbox beside each" into one scope-bound preview + a
 * capped-concurrency run over the categories that have none.
 *
 * ⛔ THIS DOES NOT HAVE A SYNCHRONOUS COMPLETION SIGNAL, UNLIKE PersonaRewriteEnqueue.
 * The underlying research endpoint (POST /:sectorId/attribute-intelligence) answers
 * "processing" immediately and finishes 1-2 minutes later in the background, with no
 * session/job row anywhere. "Unconfirmed" below means no evidence of completion
 * appeared within the wait window — it is NOT a report of failure. Leaving this tab
 * or closing the browser stops the orchestration loop (already-started categories
 * keep running server-side regardless); re-running Preview is how you resume.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { useSite } from '@/contexts/SiteContext';
import { categoryWriteKey } from '@/components/category/categoryWriteKey';
import {
  scopeKey,
  canWrite,
  parseCoveragePreview,
  runOneTick,
  type PreviewState,
  type RunRow,
  type TickEffects,
} from './AttributeIntelligenceBulkResearch.logic';

export interface AttributeIntelligenceBulkResearchProps {
  sectorId: number | null;
  categories: Array<{ categoryPath: string }>;
  /**
   * Ruling (v): the catalogue roots of the site (lower-cased, separator-free). A previewed path with no
   * separator is POSTed only when it is one of these; absent (undefined/null) means "not loaded", and such a
   * path is refused on its row rather than guessed at.
   */
  catalogueRoots?: ReadonlySet<string> | null;
  /**
   * ⛔⛔ DOES `categories` ACTUALLY BELONG TO `sectorId`? THE SCOPE KEY CANNOT ANSWER THIS
   * (codex-reviewer-2). scopeKey compares sectorId+siteId — identifiers only — so a preview
   * fired with the RIGHT sectorId and the PREVIOUS sector's categories reads to it as
   * perfectly fresh and in-scope. The page's `sectorId` updates the instant the operator
   * switches sector; its `categories` state keeps the old sector's rows until loadData()'s
   * fetch resolves. That window is enough to fire paid AI research for one sector's
   * categories under another sector's label, with nothing in this component able to notice.
   *
   * ⇒ Required, and it must be the page's OWN `categoriesKnown` flag — false at the start of
   * every load, true only once the categories fetch resolves — the same one
   * researchExistsFor / researchExistsInSector already gate on. Required rather than
   * defaulted to true, because a default would silently reinstate the hazard at any future
   * mount site that forgets it: unknown must be able to BE unknown.
   */
  categoriesKnown: boolean;
  /** Real interval between ticks. Defaults to 10s in production; tests override
   *  this to a few ms so the run's tests don't wait on real 10-second timers —
   *  the tick CADENCE is not what's under test (that's pinned, timer-free, in
   *  AttributeIntelligenceBulkResearch.logic.test.ts), only the WIRING is. */
  tickMs?: number;
}

const DEFAULT_TICK_MS = 10_000;

/**
 * ⛔ EVERY PER-TICK REQUEST MUST SETTLE INSIDE THE TICK INTERVAL. Both codex reviews
 * found the same freeze: the tick callback skips a tick while the previous one is
 * still in flight (tickInFlightRef — correct, and load-bearing for the concurrency
 * cap), so ONE unbounded request that never settles left that flag true FOREVER. No
 * tick ever ran again ⇒ the 18-tick timeout never advanced, rows never reached
 * 'unconfirmed', Stop (which only sets a ref the NEXT tick reads) became inert, and
 * the write button — gated on `running` — stayed disabled with no in-panel recovery.
 *
 * Derived from tickMs rather than written as a standalone constant so the invariant
 * "a request settles before the next tick is due" is STRUCTURAL, not a coincidence of
 * two numbers that can drift apart. At the production 10s tick that is 8s / 9s.
 */
/** Bounds the NETWORK leg, inside authFetch: aborts the underlying fetch, which frees
 *  the socket rather than merely ignoring it, and rejects with authFetch's message. */
const fetchTimeoutFor = (tickMs: number) => Math.max(1, Math.round(tickMs * 0.8));
/** Bounds the WHOLE effect. ⚠️ authFetch's own timeout is NOT sufficient on its own:
 *  it clearTimeout()s the moment `fetch` resolves, i.e. before the caller reads the
 *  body — so a response whose body never finishes streaming (`await res.json()`)
 *  stays unbounded and hangs the tick loop exactly as fatally. Strictly later than
 *  fetchTimeoutFor so the more specific network-timeout message wins when it applies. */
const effectDeadlineFor = (tickMs: number) => Math.max(2, Math.round(tickMs * 0.9));

/**
 * ⛔ THE PREVIEW NEEDS A DEADLINE TOO, AND IT CANNOT BE THE TICK'S (both codex reviews
 * on PR #5470, independently). runPreview was the one request left with no bound at
 * all: a stalled fetch — or a response whose body never finishes streaming — left
 * busyPreview true forever, so the Preview button stayed disabled reading "Checking…"
 * with no error and no in-panel recovery. A page reload was the only escape.
 *
 * ⚠️ DELIBERATELY MUCH LONGER THAN effectDeadlineFor. The tick deadlines are derived
 * from tickMs because a per-tick request MUST settle before the next tick is due. The
 * preview has no such constraint: it is a one-shot, user-initiated request that covers
 * the whole sector in a single call — up to the route's 500-path cap, served in batches
 * of 25 — so it may legitimately take far longer than any single tick. 45s bounds a
 * genuinely stuck request while leaving a real 500-path read ample room; a value near
 * the tick's would time out honest previews, which is a worse failure than the one
 * being fixed.
 */
const PREVIEW_FETCH_TIMEOUT_MS = 40_000;
const PREVIEW_DEADLINE_MS = 45_000;

/**
 * Rejects if `fn` has not settled within `ms`, and CANCELS it rather than merely
 * abandoning it — the supplied signal is handed to authFetch, which already honours an
 * external AbortSignal in addition to its own timeout. Abandoning would unblock the
 * tick loop (the freeze) but leave the socket held against the browser's per-host
 * connection budget for as long as the server kept it, which under the network
 * partition this exists for is indefinitely.
 *
 * ⚠️ The message says "deadline", not "tick deadline": this now bounds the preview too,
 * which is not on a tick.
 */
function withDeadline<T>(ms: number, label: string, fn: (signal: AbortSignal) => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`${label} did not answer within the ${ms}ms deadline`));
    }, ms);
    fn(controller.signal).then(
      (value) => { clearTimeout(timer); resolve(value); },
      (err) => { clearTimeout(timer); reject(err); },
    );
  });
}

export function AttributeIntelligenceBulkResearch({ sectorId, categories, categoriesKnown, tickMs = DEFAULT_TICK_MS, catalogueRoots = null }: AttributeIntelligenceBulkResearchProps) {
  const { selectedSite } = useSite();
  const siteId: string | null = selectedSite?.id ?? (selectedSite as any)?._id ?? null;
  const currentScopeKey = scopeKey(sectorId, siteId);

  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [previewError, setPreviewError] = useState<{ scopeKey: string; message: string } | null>(null);
  const [busyPreview, setBusyPreview] = useState(false);

  /**
   * ⛔⛔ THE SCOPE KEY CANNOT SEE A SAME-SECTOR REFRESH (Fix P, codex-reviewer, blocking).
   * scopeKey is sectorId+siteId, so a reload of the sector ALREADY on screen leaves it
   * byte-identical — and CategoryResearch.tsx has ~12 call sites that re-invoke loadData()
   * for exactly that sector (a repair, a batch-poll tick, a post-action refresh). Every one
   * of them commits a NEW array (`setCategories(categoriesData.data || [])`, a fresh array
   * off a fresh JSON parse — never an in-place mutation). After such a reload the preview's
   * `items` describe the OLD snapshot: a category may have been renamed, removed, or newly
   * appeared. The write button stayed enabled through all of it and would have fired paid,
   * sector-wide research against a list nobody is looking at any more.
   *
   * ⇒ The preview is bound to the `categories` REFERENCE it was taken against.
   *
   * ⚠️ REFERENCE IDENTITY, DELIBERATELY — NOT A DEEP CONTENT COMPARE. Any reload invalidates,
   * including one that fetched byte-identical rows. That is the same conservative discipline
   * `categoriesKnown` already applies (it drops to false on EVERY load, not only on loads that
   * changed something): unknown must re-confirm. The cost of a spurious invalidation is one
   * click on Preview; the cost of a missed one is a paid run against a stale list.
   *
   * ⚠️ AND IT COVERS THE IN-FLIGHT CASE WITH NO TIMING SPECIAL-CASE. A preview fired against
   * array A that lands after the prop has become array B is caught because `writable` below is
   * DERIVED FRESH ON EVERY RENDER — a comparison, never a stored boolean, exactly as canWrite
   * already is for scope. The first render after the response commits already evaluates false;
   * there is no window in which it is true.
   */
  const previewCategoriesRef = useRef<AttributeIntelligenceBulkResearchProps['categories'] | null>(null);

  /**
   * ⛔ ONE STALE STATE, TWO CAUSES — NOT TWO NOTICES. From the operator's side "the sector or
   * site changed" and "the category list was reloaded" are the identical fact: your preview no
   * longer describes what is on screen. They share one derivation, one testid and one wording,
   * so neither cause can regress without the other's tests noticing.
   */
  const previewMatchesCategories = previewCategoriesRef.current === categories;
  const writable = canWrite(preview, currentScopeKey) && previewMatchesCategories;
  const staleNoticeVisible = preview !== null && !writable;
  const errMsg = previewError?.scopeKey === currentScopeKey ? previewError.message : null;

  // ⛔ A CATEGORY WITH NO PATH CANNOT BE RESEARCHED OR CHECKED, and it must not be
  // posted either: the coverage route now rejects the WHOLE request if any element is
  // not a non-empty string, so one junk row would take the panel down for the entire
  // sector. Measured on db2 2026-08-27: 3 of 2,313 category_characteristics rows have
  // an empty category_path (sectors 73, 77, 84) — and they have an empty category_name
  // too, so there is genuinely nothing to research. Excluded here and SAID OUT LOUD
  // below, never silently: an invisible skip is this panel's whole failure mode.
  const researchablePaths = categories
    .map(c => c.categoryPath)
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0);
  const pathlessCount = categories.length - researchablePaths.length;

  async function runPreview() {
    const firedAt = currentScopeKey;
    // ⛔ CAPTURED HERE, AT FIRE TIME, AND NOT RE-READ LATER — that is the whole point. This is
    // the exact array `researchablePaths` (below, in the request body) was derived from; reading
    // `categories` again in the continuation after the await would read whatever the prop has
    // become by then, which is precisely the stale value this guard exists to detect.
    const firedAgainst = categories;
    const requestedPaths = researchablePaths;
    setBusyPreview(true);
    setPreviewError(null);
    setPreview(null);
    previewCategoriesRef.current = null;
    try {
      // Bounded by PREVIEW_DEADLINE_MS — the SAME withDeadline the tick loop uses, at a
      // deliberately longer value (see the constant). A timeout lands in the catch
      // below and is shown through the existing bulk-ai-error path, exactly like any
      // other preview failure: this bounds an existing failure mode, it adds none.
      const json = await withDeadline(PREVIEW_DEADLINE_MS, 'the coverage preview', async (signal) => {
        const res = await authFetch(
          `${API_URL}/category-research/${sectorId}/attribute-intelligence-coverage`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ siteId, categoryPaths: requestedPaths }),
            timeout: PREVIEW_FETCH_TIMEOUT_MS,
            signal,
          },
        );
        const body = await res.json();
        if (!res.ok) throw new Error(body?.error || `Coverage check failed (HTTP ${res.status})`);
        return body;
      });
      // ⛔ THE SAME `requestedPaths` THAT WENT OUT IN THE BODY, so the parser can check the
      // answer against the question (Fix Q): missing+covered must be an exact partition of it.
      const parsed = parseCoveragePreview(json, firedAt, requestedPaths);
      // ⚠️ THE ORDER OF THESE TWO IS NOT LOAD-BEARING, AND SAYING SO IS THE POINT — measured by
      // swapping them: all 86 tests stay green. Both are synchronous statements in one block, so
      // no render can observe a half-applied pair. It is written ref-first anyway, so that if a
      // future edit ever puts an await between them the intermediate state is the SAFE one (ref
      // updated, preview still null ⇒ writable false) rather than the unsafe one.
      previewCategoriesRef.current = firedAgainst;
      setPreview(parsed);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setPreviewError({ scopeKey: firedAt, message: msg });
      setPreview(null);
      previewCategoriesRef.current = null;
    } finally {
      setBusyPreview(false);
    }
  }

  /**
   * ⛔ THREE REASONS THE PANEL IS NOT READY, ONE INERT SHELL — deliberately the SAME branch and
   * the same `bulk-ai-not-ready` testid the no-site case already used, rather than a second
   * convention. All three mean the identical thing operationally: there is no scope this panel can
   * honestly fire a paid, sector-wide research run against, so it renders no controls at all
   * rather than clickable buttons beside a warning. The MESSAGE distinguishes them, because
   * "pick a site", "pick a sector" and "wait a moment" ask different things of the operator.
   *
   * ⛔⛔ `!sectorId` IS THE THIRD, AND IT IS THIS COMPONENT'S OWN GATE, NOT AN ECHO OF THE PAGE'S
   * (codex-reviewer, blocking; codex-reviewer-2, minor — the same defect, and it is cheap enough
   * that the severity disagreement does not decide it). sectorId is interpolated straight into the
   * request URL, so a null sector does not fail loudly: it builds
   * `/category-research/null/attribute-intelligence-coverage` and fires a real, paid, unscoped
   * request. The page's loadData() now invalidates any in-flight load when it moves to a no-sector
   * state, so stale categories can no longer arrive here marked known — but that is the page's
   * layer. This one holds even at a mount site that does not have the page's `!sectorId` render
   * guard in front of it, and even if the generation counter regressed.
   *
   * Site first, then sector: with no site there is no scope at all, which is the more fundamental
   * answer, and a sector without a site is still unscoped.
   */
  if (!siteId || !sectorId || !categoriesKnown) {
    return (
      <Card className="mb-4">
        <CardHeader><CardTitle className="text-base">Bulk research attribute intelligence</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-amber-600" data-testid="bulk-ai-not-ready">
            {!siteId
              ? 'Select a site in the header first.'
              : !sectorId
                ? 'No sector is resolved for this site, so there is nothing a sector-wide research run could be scoped to. Assign a sector to the site, or pick one on this page, first.'
                : /* ⚠️ "not loaded yet", NOT "loading": categoriesKnown is also false when no sector has
                     resolved for this site at all, and loadData() then never runs. Both mean the same
                     thing to the operator — wait — but only one of them is TRUE in both cases. */
                  "This sector's categories are not loaded yet. The list on screen may still belong to the sector you switched away from, so the preview is held until it is known."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-4" data-testid="attribute-intelligence-bulk-research">
      <CardHeader><CardTitle className="text-base">Bulk research attribute intelligence</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Researches every category in this sector that has none. Never deletes or re-researches a
          category that already has data — use the per-category control for that.
        </p>

        {/* The non-durability caveat is a REQUIREMENT OF THE SPEC, and it was only ever
            written in this file's JSDoc — where no operator can read it. An operator who
            does not know the loop dies with the tab has no way to tell a closed tab from a
            finished run. */}
        <p className="text-xs text-muted-foreground" data-testid="bulk-ai-not-durable">
          This is not a durable job: leaving this tab or closing the browser stops the orchestration
          loop. Categories that already started keep running on the server regardless, and
          re-running Preview is how you resume.
        </p>

        {pathlessCount > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="bulk-ai-pathless-note">
            {pathlessCount} categor{pathlessCount === 1 ? 'y has' : 'ies have'} no category path recorded
            and {pathlessCount === 1 ? 'is' : 'are'} excluded from this preview — there is nothing to
            research or check for {pathlessCount === 1 ? 'it' : 'them'}. Fix the category record itself
            if {pathlessCount === 1 ? 'it' : 'that'} looks wrong.
          </p>
        )}

        <Button variant="outline" disabled={busyPreview} onClick={runPreview} data-testid="bulk-ai-preview">
          {busyPreview ? 'Checking…' : '1. Preview — writes nothing'}
        </Button>

        {staleNoticeVisible && (
          <div className="rounded border border-amber-500 p-3 text-sm text-amber-700" data-testid="bulk-ai-preview-stale">
            The sector, the site, or the category list on this page changed since this preview ran,
            so it no longer describes what is on screen. Re-run the preview.
          </div>
        )}

        {preview && writable && (
          <div className="rounded border p-3 text-sm" data-testid="bulk-ai-preview-result">
            <div className="mb-2 font-medium">
              Would research <Badge variant="secondary">{preview.count}</Badge> categories
            </div>
            <ul className="max-h-40 list-disc overflow-auto pl-5 text-muted-foreground">
              {preview.items.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        )}

        <RunPanel
          catalogueRoots={catalogueRoots}
          key={currentScopeKey}
          preview={preview}
          writable={writable}
          sectorId={sectorId}
          siteId={siteId}
          tickMs={tickMs}
        />

        {errMsg && <p className="text-sm text-destructive" data-testid="bulk-ai-error">{errMsg}</p>}
      </CardContent>
    </Card>
  );
}

/**
 * Isolated so the tick interval's lifetime is tied to a fresh mount per scope
 * (the `key={currentScopeKey}` above) rather than to manual reset logic — a run
 * started at one scope cannot leak into another simply by remounting.
 */
function RunPanel({
  preview,
  writable,
  sectorId,
  siteId,
  tickMs,
  catalogueRoots,
}: {
  preview: PreviewState | null;
  /** ruling (v): the site's separator-free catalogue names; null = not loaded (a bare path is refused, not guessed) */
  catalogueRoots: ReadonlySet<string> | null;
  writable: boolean;
  sectorId: number | null;
  siteId: string;
  tickMs: number;
}) {
  const [rows, setRows] = useState<RunRow[]>([]);
  const [running, setRunning] = useState(false);
  const [dupPathCount, setDupPathCount] = useState(0);
  const rowsRef = useRef<RunRow[]>([]);
  // ⛔ THE LAST REQUEST ERROR PER PATH — the ONE thing about a stuck row that is
  // genuinely observable. runOneTick deliberately swallows these rejections and judges
  // a row purely by its timeout (the research endpoint has no completion signal, so
  // "the research failed" is never claimed, and that stays true). But the network CALL
  // either succeeded or threw, and discarding that made a backend that 500s on every
  // request look IDENTICAL to "the AI is just slow" for the full 3-minute window.
  // Recorded here, rendered beside the status. A ref, not state: it must never drive
  // the tick, only the display, and the tick's own setRows(next) already re-renders
  // every tick.
  //
  // ⛔⛔ TWO SLOTS, NOT ONE (codex-reviewer-2). The first version kept a single string
  // per path, cleared by ANY subsequent success for that path — and a coverage check
  // answering "not covered yet" is a perfectly normal SUCCESS. So the tick right after
  // a failed research POST erased the only evidence that the research never started,
  // and the operator was back to reading a plain "researching (in-flight)" for the
  // full 3-minute window: the exact silent fallback this display exists to close,
  // recreated inside the display's own bookkeeping. A start error is now cleared ONLY
  // by a successful start, a poll error ONLY by a successful poll.
  type PathErrors = { start?: string; poll?: string };
  const lastErrorRef = useRef<Record<string, PathErrors>>({});
  const tickRef = useRef(0);
  const stopRequestedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // ⛔ GUARDS AGAINST OVERLAPPING TICKS. If a tick's network calls hang (a real
  // possibility — a network partition, not just a test artifact), setInterval fires
  // the NEXT tick anyway while the first is still in flight. Without this guard, that
  // second tick reads the same stale rowsRef (still 'queued', since the first tick's
  // setRows never ran) and fires ANOTHER 4 — the concurrency cap this component
  // exists to enforce would silently stop capping under exactly the failure mode
  // most worth capping for.
  const tickInFlightRef = useRef(false);

  useEffect(() => { rowsRef.current = rows; }, [rows]);

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  /**
   * Records whether the CALL threw, then hands back the identical outcome. Purely
   * additive: the value returned (and the exception re-thrown) is byte-for-byte what
   * the unwrapped effect produced, so runOneTick's swallow-and-judge-by-timeout
   * behaviour is unchanged — see lastErrorRef above.
   *
   * `kind` is what keeps a successful poll from erasing a start failure: a success
   * clears its OWN kind's slot only.
   */
  async function observe<T>(kind: keyof PathErrors, path: string, fn: () => Promise<T>): Promise<T> {
    try {
      const out = await fn();
      // A later success of the SAME kind clears an earlier blip of that kind — a
      // transient failure that recovered must not keep reading as broken for the rest
      // of the run.
      const slot = lastErrorRef.current[path];
      if (slot) {
        delete slot[kind];
        if (slot.start === undefined && slot.poll === undefined) delete lastErrorRef.current[path];
      }
      return out;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      lastErrorRef.current[path] = { ...(lastErrorRef.current[path] ?? {}), [kind]: message };
      throw err;
    }
  }

  function effectsFor(): TickEffects {
    // Recomputed per tick from the live prop so a tickMs change can never leave a
    // deadline longer than the interval it is supposed to fit inside.
    const fetchTimeout = fetchTimeoutFor(tickMs);
    const deadline = effectDeadlineFor(tickMs);
    return {
      startResearch: (path) => observe('start', path, () => withDeadline(deadline, 'the research request', async (signal) => {
        // B2 (kexnycgswp): the FULL PATH is the key — this used to POST the last segment, which wrote
        // domain_knowledge under a bare leaf. A refusal is thrown so it lands on the row as "start failed".
        const writeKey = categoryWriteKey(path, catalogueRoots ?? null);
        if (!writeKey.ok) throw new Error(writeKey.reason);
        const res = await authFetch(`${API_URL}/category-research/${sectorId}/attribute-intelligence`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categoryName: writeKey.key, siteId, depth: 'standard', forceRegenerate: false }),
          timeout: fetchTimeout,
          signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      })),
      checkCoverage: (path) => observe('poll', path, () => withDeadline(deadline, 'the coverage check', async (signal) => {
        const res = await authFetch(`${API_URL}/category-research/${sectorId}/attribute-intelligence-coverage`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ siteId, categoryPaths: [path] }),
          timeout: fetchTimeout,
          signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        // ⛔ FAIL CLOSED, same strictness as parseCoveragePreview: a body that does not
        // affirmatively report success is not confirmation, however friendly its
        // `covered` array looks.
        //
        // ⚠️ THROWS rather than returning false (codex-reviewer-2). The OUTCOME is
        // identical — runOneTick's `.catch(() => false)` still means "not confirmed",
        // and the row is still judged purely by its timeout — but returning false made
        // a backend answering garbage byte-identical to one honestly answering "not
        // covered yet", so the anomaly never reached observe() and never reached the
        // operator. A throw records it; it changes nothing else.
        if (json?.success !== true || !Array.isArray(json?.covered)) {
          throw new Error('malformed coverage response');
        }
        return json.covered.includes(path);
      })),
    };
  }

  function start() {
    if (!writable || !preview) return;
    // Belt-and-suspenders: even if the UI guard (disabled={!writable || running})
    // is somehow bypassed, start() itself must not be able to create a second live
    // interval — an orphaned prior interval would double-fire research POSTs past
    // the concurrency cap and halve the effective MAX_TICKS_IN_FLIGHT timeout.
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    // B2 (kexnycgswp, ruling 2026-09-12: full path everywhere): the run list is deduplicated by EXACT PATH
    // only. This used to dedupe BY LEAF NAME, because the POST sent the leaf and one leaf-keyed row covered
    // every sibling path — under full-path keys two paths sharing a leaf are two categories, each researched
    // in its own right. A preview cannot normally repeat a path; the guard stays so a repeated one is queued
    // once and the count is STATED, never silent.
    const seenPath = new Set<string>();
    const items = preview.items.filter((p) => {
      const key = p.trim().toLowerCase();
      if (seenPath.has(key)) return false;
      seenPath.add(key);
      return true;
    });
    setDupPathCount(preview.items.length - items.length);
    setRows(items.map(path => ({ path, status: 'queued', startedAtTick: null })));
    lastErrorRef.current = {};
    stopRequestedRef.current = false;
    tickRef.current = 0;
    setRunning(true);

    intervalRef.current = setInterval(async () => {
      if (tickInFlightRef.current) return; // previous tick still running — never overlap
      tickInFlightRef.current = true;
      try {
        tickRef.current += 1;
        const next = await runOneTick(rowsRef.current, tickRef.current, stopRequestedRef.current, effectsFor());
        rowsRef.current = next;
        setRows(next);
        // Once Stop has been requested, computeTick (Task 2) never again includes a
        // still-'queued' row in toStart/toCheck/toTimeout — by design, a stopped run
        // must never start a queued category. That leaves such rows permanently
        // 'queued', which would otherwise make `allTerminal` never true and strand
        // `running` at true forever (write button stuck disabled with no in-panel
        // recovery). So after a stop, a 'queued' row also counts as resolved FOR
        // COMPLETION PURPOSES ONLY — its visible status stays 'queued' (honest: it
        // truly was never attempted), this only lets the run finish and re-enable
        // the write button.
        const allTerminal = next.every(r =>
          r.status === 'confirmed' ||
          r.status === 'unconfirmed' ||
          (stopRequestedRef.current && r.status === 'queued'),
        );
        if (allTerminal && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          setRunning(false);
        }
      } finally {
        tickInFlightRef.current = false;
      }
    }, tickMs);
  }

  function stop() {
    stopRequestedRef.current = true;
  }

  const statusLabel: Record<RunRow['status'], string> = {
    queued: 'queued',
    'in-flight': 'researching (in-flight)',
    confirmed: 'confirmed',
    unconfirmed: 'unconfirmed — no result within the wait window (not a failure report)',
  };

  return (
    <>
      <Button variant="default" disabled={!writable || running} onClick={start} data-testid="bulk-ai-write">
        {writable && preview
          ? `2. Research ${preview.count} categor${preview.count === 1 ? 'y' : 'ies'}`
          : '2. Research — run a preview first'}
      </Button>

      {running && (
        <Button variant="outline" onClick={stop} data-testid="bulk-ai-stop">
          Stop — finish in-flight only
        </Button>
      )}

      {dupPathCount > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="bulk-ai-dedup-note">
          {dupPathCount} categor{dupPathCount === 1 ? 'y' : 'ies'} appeared more than once in the preview (same path) and
          {dupPathCount === 1 ? ' was' : ' were'} queued once. Paths that only share a leaf name are different categories and are each researched.
        </p>
      )}

      {rows.length > 0 && (
        <ul className="max-h-60 overflow-auto text-sm" data-testid="bulk-ai-run-rows">
          {rows.map(r => {
            // Only meaningful while a row is unresolved: a 'confirmed' row's last call
            // succeeded by definition, and a 'queued' row has made no calls at all.
            const errors = (r.status === 'in-flight' || r.status === 'unconfirmed')
              ? lastErrorRef.current[r.path]
              : undefined;
            // BOTH are shown when both are present — neither failure may hide the other.
            const parts: string[] = [];
            if (errors?.start) parts.push(`start failed: ${errors.start}`);
            if (errors?.poll) parts.push(`last check failed: ${errors.poll}`);
            return (
              <li key={r.path} data-testid={`bulk-ai-row-${r.path}`}>
                {r.path} — {statusLabel[r.status]}
                {parts.length > 0 ? ` — ${parts.join('; ')}` : ''}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

export default AttributeIntelligenceBulkResearch;
